// src/pages/SuperAdmin/Reservations/reservationsApi.js
import api from "../../../components/axiosComp";

/**
 * READ (list)
 * Works with either {items,total,page,pageSize} or a raw array from the backend.
 * Accepts optional filters: { page, pageSize, q, status, lifecycle, venue, venueGround, userId, from, to }
 */
export async function listReservations(params = {}) {
  const {
    page = 1,
    pageSize = 50,
    q = "",
    status,
    lifecycle,
    venue,
    venueGround,
    userId,
    from,
    to,
    isAdminReservation,
    isUserReservation,
    range,

    // ✅ NEW: sorting params
    sortBy,
    sortDir,
  } = params;

  const { data } = await api.get("/api/reservations", {
    params: Object.fromEntries(
      Object.entries({
        page,
        limit: pageSize,
        q,
        status,
        lifecycle,
        venue,
        venueGround,
        userId,
        from,
        to,
        range,
        isAdminReservation,
        isUserReservation,

        // ✅ send these to backend
        sortBy,
        sortDir,
      }).filter(([, v]) => v !== undefined && v !== null && v !== "")
    ),
  });

  const items = Array.isArray(data?.items)
    ? data.items
    : Array.isArray(data)
      ? data
      : [];

  return {
    items,
    total: data?.total ?? items.length,
    page: data?.page ?? page,
    pageSize: data?.limit ?? pageSize,
  };
}


/**
 * STATS (KPI cards + tab totals)
 * Returns:
 * {
 *  today, tomorrow, thisWeek, newToday, totalBookings,
 *  userTotalBookings, adminTotalBookings
 * }
 */
export async function getReservationsStats(params = {}) {
  const {
    status,
    venue,
    venueGround,
    userId,
    city,
    country,
    bookingId,
    booking_id,
    // IMPORTANT: DO NOT SEND these to stats (stats must be global)
    // isAdminReservation,
    // isUserReservation,
  } = params;

  const { data } = await api.get("/api/reservations/stats", {
    params: Object.fromEntries(
      Object.entries({
        status,
        venue,
        venueGround,
        userId,
        city,
        country,
        bookingId,
        booking_id,
        // do NOT include tab flags here
      }).filter(([, v]) => v !== undefined && v !== null && v !== "")
    ),
  });

  return {
    today: Number(data?.today || 0),
    tomorrow: Number(data?.tomorrow || 0),
    thisWeek: Number(data?.thisWeek || 0),
    newToday: Number(data?.newToday || 0),
    totalBookings: Number(data?.totalBookings || 0),

    userTotalBookings: Number(data?.userTotalBookings || 0),
    adminTotalBookings: Number(data?.adminTotalBookings || 0),

    userTodayBookings: Number(data?.userTodayBookings || 0),
    adminTodayBookings: Number(data?.adminTodayBookings || 0),

    userTomorrowBookings: Number(data?.userTomorrowBookings || 0),
    adminTomorrowBookings: Number(data?.adminTomorrowBookings || 0),

    userThisWeekBookings: Number(data?.userThisWeekBookings || 0),
    adminThisWeekBookings: Number(data?.adminThisWeekBookings || 0),

    userNewTodayBookings: Number(data?.userNewTodayBookings || 0),
    adminNewTodayBookings: Number(data?.adminNewTodayBookings || 0),
  };
}


/**
 * READ (single)
 */
export async function getReservation(id) {
  const { data } = await api.get(`/api/reservations/${id}`);
  // normalize to always return the doc
  return data?.reservation ?? data;
}




export async function createReservation(payload) {
  // ✅ Backend decides admin/user based on token role (req.auth.role).
  // Frontend should NOT send isAdminReservation.
  const { data } = await api.post("/api/reservations", payload);
  return data?.reservation ?? data;
}


/**
 * UPDATE (partial)
 */
export async function updateReservation(id, payload) {
  const { data } = await api.patch(`/api/reservations/${id}`, payload);
  return data?.reservation ?? data;
}

/**
 * CANCEL (soft)
 */
export async function cancelReservation(id, payload = {}) {
  const { data } = await api.delete(`/api/reservations/${id}`, {
    data: payload,   // 👈 send { reason }
  });

  return {
    ok: true,
    message: data?.message ?? "Reservation cancelled",
    reservationId: data?.reservationId ?? id,
  };
}


/**
 * Utility — reserved slots (for UI)
 */
export async function getReservedSlots(venueGround, date, viewerUserId) {
  const params = { venueGround, date };
  if (viewerUserId) params.viewerUserId = viewerUserId;
  const { data } = await api.get(`/api/reservations/reserved-slots`, {
    params,
  });
  return data;
}

// ✅ NEW: Slots as per venue open/close time (SOURCE OF TRUTH)
export async function getGroundSlotsForDate(slug, groundId, date, slotSize = 60, timezone) {
  const params = { date, slotSize };

  // Optional (BE already falls back, but okay to pass)
  if (timezone) params.timezone = timezone;

  // Display-only (BE echoes back)
  // Convert browser offset to "+05:30"
  const off = -new Date().getTimezoneOffset(); // minutes ahead of UTC
  const sign = off >= 0 ? "+" : "-";
  const abs = Math.abs(off);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");
  params.clientTz = `${sign}${hh}:${mm}`;

  const { data } = await api.get(`/api/venues/${slug}/grounds/${groundId}/slots`, {
    params,
  });

  return data; // { slots: [...], venue: {...}, ground: {...}, slotSize, ... }
}


/**
 * Helpers to fetch venues/grounds for selects (admin endpoints you already have)
 */
export async function listVenuesBasic(params = {}) {
  const { page = 1, pageSize = 200, q = "" } = params;
  const { data } = await api.get("/api/superAdmin/venues", {
    params: { page, pageSize, q },
  });
  const items = Array.isArray(data?.items)
    ? data.items
    : Array.isArray(data)
      ? data
      : [];
  return items;
}

// If your VenueDetails returns grounds, reuse it; otherwise create a dedicated admin endpoint.
export async function listVenueGrounds(venueId) {
  const { data } = await api.get(`/api/superAdmin/venues/${venueId}`);
  // try common shapes: { venue: { grounds: [] }} or { grounds: [] }
  const venue = data?.venue ?? data;
  const grounds = Array.isArray(venue?.grounds)
    ? venue.grounds
    : Array.isArray(data?.grounds)
      ? data.grounds
      : [];
  return grounds;
}

// add near other exports
export async function confirmReservation(id) {
  // confirm = set reservationStatus -> confirmed, paymentStatus -> completed
  return updateReservation(id, {
    reservationStatus: "confirmed",
    paymentStatus: "completed",
  });
}

export async function expireReservation(id) {
  // if you need to force-expire (admin)
  return updateReservation(id, { reservationStatus: "expired" });
}

// --- RECURRING HOLDS ---
// Create a recurring hold for a user (weekly)
export async function createRecurringHold(payload) {
  // payload: { userId, venueGround, dayOfWeek, timeSlots, totalAmount }
  const { data } = await api.post("/api/reservations/recurring/hold", payload);
  return data?.reservation ?? data;
}

// List upcoming grouped reservations for a user
export async function listUpcomingReservations(userId, from, to) {
  const { data } = await api.get("/api/reservations/upcoming", {
    params: { userId, from, to },
  });
  return data;
}

// Optional: admin quick confirm of a held reservation
export async function adminConfirmReservation(id) {
  const { data } = await api.patch(`/api/reservations/${id}`, {
    reservationStatus: "confirmed",
    paymentStatus: "completed",
  });
  return data?.reservation ?? data;
}

// ---- USERS (client-side filter) ----
let _usersCache = null; // in-memory cache for this tab

export async function getAllUsersBasic() {
  if (_usersCache) return _usersCache;
  const { data } = await api.get("/api/auth/users");
  const arr = Array.isArray(data?.users) ? data.users : [];
  // normalize (only the fields we need)
_usersCache = arr.map((u) => ({
  id: u._id,
  name: u.fullName || "",
  phone: u.mobileNumber || "",
  email: u.email || "",
  countryCode: u.countryCode || "",
  phoneE164: u.phoneE164 || "",
}));
  return _usersCache;
}

// cheap client-side fuzzy-ish filter
export async function searchUsersClient(q) {
  const all = await getAllUsersBasic();
  const s = String(q || "")
    .trim()
    .toLowerCase();
  if (!s) return [];
  // score across fields; keep simple & fast
  const scored = all
    .map((u) => {
      const hay = `${u.name} ${u.phone} ${u.email} ${u.countryCode} ${u.phoneE164}`.toLowerCase();
      const hit = hay.includes(s);
      return { u, hit };
    })
    .filter((x) => x.hit)
    .slice(0, 20)
    .map((x) => x.u);
  return scored;
}
